import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';
import { Spin } from 'antd';

import { getMyProfile, ILoginState, IMenuState, IMyProfileState } from '@share/store/slices';
import { Loading } from '@share/components';
import { RENEWAL_AGENCY_EXCLUDE_RENEWAL, RENEWAL_AGENCY_INCLUDE_RENEWAL } from '@share/constants';
import { FunctionalRootState, RootState } from '@share/utils';

import { getRenewalPaymentUrl } from '../../../my-profile/utils';

import { AccountPaymentMethods } from '../payment-methods';
import { AccountAuthorizedUsers } from './authorized-users-list';
import { AccountProfileForm } from './profile-form';

import styles from './styles.module.scss';

export type ProfileSectionPart = 'profile' | 'authorized' | 'payment';

interface IProps {
  part: ProfileSectionPart;
}

/**
 * The three parts of the legacy `MyProfile` tabs as separate hub sections. The member profile is
 * loaded here once, the way `MyProfile` does, because the three children only read the store.
 */
export const AccountProfileSection = ({ part }: IProps): React.ReactElement => {
  const intl = useIntl();
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();

  const loginStore: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);
  const menuStore: IMenuState = useSelector((state: FunctionalRootState) => state.navigationMenuStore);
  const myProfileStore: IMyProfileState = useSelector((state: FunctionalRootState) => state.myProfileStore);

  useEffect(() => {
    if (!loginStore.loading) {
      dispatch(getMyProfile());
    }
  }, [loginStore.loading]);

  const { loading, loadingCards, result } = myProfileStore;
  const profile = result?.profile;

  const renewalAgency = menuStore?.items?.renewalAgency as string;
  const isRenewalMember = RENEWAL_AGENCY_INCLUDE_RENEWAL.includes(renewalAgency) && !RENEWAL_AGENCY_EXCLUDE_RENEWAL.includes(renewalAgency);
  const showMembership = part === 'profile' && !menuStore?.items?.isMLM && !!(profile?.memberSince || profile?.expirationDate);

  return (
    <div className={styles.section}>
      {showMembership ? (
        <section className={styles.membership} aria-labelledby="account-hub-membership-title">
          <h2 id="account-hub-membership-title" className={styles.label}>
            {intl.formatMessage({ id: 'my.account.hub.membership.title' })}
          </h2>
          <dl className={styles.facts}>
            {profile?.memberSince ? (
              <div>
                <dt>{intl.formatMessage({ id: 'my.account.hub.membership.since' })}</dt>
                <dd>{profile.memberSince}</dd>
              </div>
            ) : null}
            {profile?.expirationDate ? (
              <div>
                <dt>{intl.formatMessage({ id: 'my.account.hub.membership.renewal_due' })}</dt>
                <dd>{profile.expirationDate}</dd>
              </div>
            ) : null}
          </dl>
          {isRenewalMember ? (
            <a
              href={getRenewalPaymentUrl(loginStore.user?.keyid)}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.renewButton}
            >
              {intl.formatMessage({ id: 'my.account.hub.membership.pay_renewal' })}
            </a>
          ) : null}
        </section>
      ) : null}

      <Spin spinning={loading || loadingCards} indicator={<Loading />}>
        {part === 'payment' ? <AccountPaymentMethods /> : null}
        {part === 'authorized' ? <AccountAuthorizedUsers /> : null}
        {part === 'profile' ? (
          <section className={styles.card}>
            <AccountProfileForm />
          </section>
        ) : null}
      </Spin>
    </div>
  );
};
